import { action, computed, observable } from 'mobx';
import { IRootStore } from '@stores/index';
import { apiAuthCaller } from '@services/apiCaller';
import { AxiosResponse } from 'axios';
import { disableBilling } from '@config';
import moment, { Moment } from 'moment';
import { EVENT_TYPES } from '@components/profile/constants';
import { getApiErrorMessage } from '@components/common/util/util';
import { serverInvoiceToClient, serverPricingToClient } from '@components/profile/billing/util';
import formatDate from '@utils/formatDateHourly';
import getRollingDates from '@utils/getRollingDates';

export interface fromTo {
  startDate: number | undefined;
  endDate: number | undefined;
}

export type EventsByType = {
  [p in keyof typeof EVENT_TYPES]: {
    totalEvents: number;
  };
};

export interface EventTypeInfo {
  EventStream: number;
  WarehouseAction: number;
  CloudExtract: number;
  Total?: number;
}

export interface IPricing {
  free: {
    cost: string;
    credits: number;
  };
  pro: {
    cost: string;
    credits: number;
  };
}

export interface ICardToken {
  id?: string;
  card?: {
    last4: string;
    brand: string;
    expiry?: string;
  };
}

export interface InvoiceInfo {
  company: string;
  address: {
    street: string;
    unit: string;
    city: string;
    state: string;
    country: string;
    postalCode: string;
  };
}

export interface IPaymentHistory {
  amountReceived: number;
  timestamp: number;
  status: string;
  paymentDetails: {
    brand?: string;
    last4: number;
  };
  nextAction: {
    type: string;
  };
  clientSecret: string;
  error?: string;
  errorCode?: string;
}

interface ISetUpIntentInfo {
  clientSecret: string;
  nextAction: string;
  status: string;
}

export type SetupIntentInfo = ISetUpIntentInfo | undefined;

export interface IBillingStore {
  rootStore: IRootStore;
  totalEventsByCategory: EventsByType;
  billingTenure: fromTo | undefined;
  planName: string;
  eventTypePercent: EventTypeInfo;
  getPlanName: () => void;
  getPlanDisplayName: string;
  redirectToBilling: () => void;
  getTenantDetail: () => void;
  getTotalEventsByCategory: (onSuccess: () => void, onError: () => void) => void;
  payments: IPaymentHistory[] | undefined;
  pricing: IPricing;
  tenantInfo: ITenantInfo | undefined;
  firstLoad: boolean;
  isShowBilling: boolean;
  isShowUsagePage: boolean;
  isFreePlan: boolean;
  isProPlan: boolean;
  getBillingDate: Moment | undefined;
  clearBillingInfo: () => void;
  hasNewBilling: boolean;
  convertToFreePlan: (onSuccess: () => void, onError: () => void) => void;
  convertToProPlan: (
    onSuccess: (setupIntent: SetupIntentInfo) => void,
    onError: () => void,
  ) => void;
  saveCardToken: (token: ICardToken) => void;
  cardToken: ICardToken | undefined;
  invoiceInfo: InvoiceInfo;
  emails: string[];
  updateInvoiceDetails: (invoice: InvoiceInfo) => void;
  credits: number;
  eventCredits: EventTypeInfo;
  costPerCredit: EventTypeInfo;
  eventsPerCredit: EventTypeInfo;
  submitInvoiceInfo: (v: InvoiceInfo, e: string[]) => void;
  submitCardInfo: (c: ICardToken, callback: (data: SetupIntentInfo) => void) => void;
  getPaymentHistory: () => void;
  setupIntent?: SetupIntentInfo;
}

interface IBillingInfoResponse {
  name: string;
  currentPeriodStartAt: number;
  currentPeriodEndAt: number;
  metadata: { [index: string]: number | string };
  invoiceInfo: InvoiceInfoResp;
  paymentMethod: ICardToken;
  pendingSetupIntentInfo: SetupIntentInfo;
  emails: string[];
}

export interface InvoiceInfoResp {
  name: string;
  address: {
    city: string;
    country: string;
    line1: string;
    line2: string;
    postalCode: string;
    state: string;
  };
}

interface ITenantInfo {
  planType: string;
  billingEnabled: boolean;
  createdAt: string;
  billingVersion: string;
  currentPlanStartAt: number;
}

interface IBillingURL {
  url: string;
}

const eventsData: EventsByType = {
  CloudExtract: {
    totalEvents: 0,
  },
  WarehouseAction: {
    totalEvents: 0,
  },
  EventStream: {
    totalEvents: 0,
  },
};

export class BillingStore implements IBillingStore {
  @observable public rootStore: IRootStore;

  @observable public planName = '';

  @observable tenantInfo: ITenantInfo | undefined;

  @observable billingTenure: fromTo | undefined;

  @observable pricing: IPricing;

  @observable public totalEventsByCategory: EventsByType = eventsData;

  @observable invoiceInfo = { address: {} } as InvoiceInfo;

  @observable emails: string[] = [];

  @observable cardToken: ICardToken | undefined;

  @observable payments: IPaymentHistory[] | undefined;

  @observable version: string | undefined;

  @observable planPrice = 0;

  @observable firstLoad = false;

  @observable public eventTypePercent: EventTypeInfo = {
    EventStream: 0,
    WarehouseAction: 0,
    CloudExtract: 0,
  };

  @observable public eventsPerCredit: EventTypeInfo = {
    EventStream: 0,
    WarehouseAction: 0,
    CloudExtract: 0,
  };

  @observable public credits = 0;

  /* (MATH.CEIL ( number * 10000 )/ 10000
  get the ceiling upto four decimals */
  @computed
  get eventCredits() {
    return {
      EventStream:
        Math.ceil(
          (this.totalEventsByCategory.EventStream.totalEvents / this.eventsPerCredit.EventStream) *
            10000,
        ) / 10000,
      CloudExtract:
        Math.ceil(
          (this.totalEventsByCategory.CloudExtract.totalEvents /
            this.eventsPerCredit.CloudExtract) *
            10000,
        ) / 10000,
      WarehouseAction:
        Math.ceil(
          (this.totalEventsByCategory.WarehouseAction.totalEvents /
            this.eventsPerCredit.WarehouseAction) *
            10000,
        ) / 10000,
      Total:
        Math.ceil(
          (this.totalEventsByCategory.WarehouseAction.totalEvents /
            this.eventsPerCredit.WarehouseAction +
            this.totalEventsByCategory.CloudExtract.totalEvents /
              this.eventsPerCredit.CloudExtract +
            this.totalEventsByCategory.EventStream.totalEvents / this.eventsPerCredit.EventStream) *
            10000,
        ) / 10000,
    };
  }

  @observable setUpIntent: SetupIntentInfo;

  // For computing the percentage of events of each type in the totalEvents.
  @action getPercentages() {
    const total =
      this.eventCredits.WarehouseAction +
      this.eventCredits.CloudExtract +
      this.eventCredits.EventStream;
    const denominator = this.credits ? Math.max(this.credits, total) : total;
    this.eventTypePercent.EventStream =
      (Number(this.eventCredits.EventStream) / denominator) * 100 || 0;
    this.eventTypePercent.CloudExtract =
      (Number(this.eventCredits.CloudExtract) / denominator) * 100 || 0;
    this.eventTypePercent.WarehouseAction =
      (Number(this.eventCredits.WarehouseAction) / denominator) * 100 || 0;
  }

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;
    this.pricing = {
      free: {
        cost: '$xx',
        credits: 0,
      },
      pro: {
        cost: '$xx',
        credits: 0,
      },
    };
  }

  @computed
  get getPlanDisplayName() {
    if (this?.tenantInfo && this.tenantInfo.billingEnabled) {
      if (this.isFreePlan) {
        return 'Free';
      }
      return this.tenantInfo.planType;
    }
    return '';
  }

  @action.bound
  // get the plan type, the number of credits and other billing info
  public async getPlanName() {
    const {
      userStore,
      organizationStore: { isOpenSource },
      messagesStore,
    } = this.rootStore;
    const { token, isAdmin } = userStore;
    if (disableBilling || isOpenSource || !isAdmin) {
      return;
    }
    try {
      const resp: AxiosResponse<IBillingInfoResponse> = await apiAuthCaller(token).get('/billing');
      this.updateBillingInfo(resp.data);
    } catch (error) {
      if (this.isShowBilling) {
        messagesStore.showErrorMessage('Failed to get billing details');
      }
    }
  }

  @action.bound
  private updateBillingInfo(subscriptionInfo: IBillingInfoResponse) {
    const {
      name,
      paymentMethod,
      invoiceInfo,
      currentPeriodStartAt,
      currentPeriodEndAt,
      metadata,
      emails,
      pendingSetupIntentInfo,
    } = subscriptionInfo;
    this.planName = name;
    if (this.isShowBilling) {
      this.updateDatesBillingPage(currentPeriodStartAt, currentPeriodEndAt);
    }
    this.cardToken = paymentMethod;
    this.invoiceInfo = serverInvoiceToClient(invoiceInfo);
    this.planPrice = Number(String(metadata.price).replace('$', ''));
    this.credits = Number(metadata.credits);
    this.eventsPerCredit = {
      CloudExtract: metadata.cloud_epc ? Number(metadata.cloud_epc) : 1,
      WarehouseAction: metadata.warehouse_epc ? Number(metadata.warehouse_epc) : 1,
      EventStream: metadata.eventstream_epc ? Number(metadata.eventstream_epc) : 1,
    };
    this.emails = emails || [];
    this.setUpIntent = pendingSetupIntentInfo;
  }

  @computed get costPerCredit() {
    return {
      EventStream: (Number(this.eventCredits.EventStream) * this.planPrice) / this.credits,
      WarehouseAction: (Number(this.eventCredits.WarehouseAction) * this.planPrice) / this.credits,
      CloudExtract: (Number(this.eventCredits.CloudExtract) * this.planPrice) / this.credits,
      Total: (Number(this.eventCredits.Total) * this.planPrice) / this.credits,
    };
  }

  @computed get isShowBilling() {
    const { userStore } = this.rootStore;
    return (
      userStore?.isTeamsAdmin &&
      this.tenantInfo !== undefined &&
      this.tenantInfo?.planType !== '' &&
      this.tenantInfo?.billingEnabled
    );
  }

  @action.bound
  public clearBillingInfo() {
    this.tenantInfo = undefined;
  }

  @computed get isShowUsagePage() {
    const {
      userStore,
      organizationStore: { isOpenSource },
    } = this.rootStore;
    return (
      userStore?.isTeamsAdmin &&
      !this.tenantInfo?.billingEnabled &&
      this.tenantInfo !== undefined &&
      !isOpenSource
    );
  }

  @computed get isFreePlan() {
    return this.tenantInfo?.planType === 'freeTier';
  }

  @computed get isProPlan() {
    return this.tenantInfo?.planType === 'pro';
  }

  @action.bound
  public async getTenantDetail() {
    const {
      userStore,
      organizationStore: { isOpenSource },
    } = this.rootStore;
    if (disableBilling || isOpenSource || !userStore.isAdmin) {
      this.firstLoad = true;
      return;
    }
    try {
      const resp: AxiosResponse<ITenantInfo> = await apiAuthCaller(userStore.token).get('/tenant');
      this.tenantInfo = resp.data;
      this.version = this.tenantInfo.billingVersion;
      if (this.hasNewBilling) {
        this.getAllPlans();
      }
      if (this.isShowUsagePage) {
        this.updateDatesUsagePage(this.tenantInfo.currentPlanStartAt);
      }
    } catch (err) {
      // handle error
    } finally {
      this.firstLoad = true;
    }
  }

  private updateDatesUsagePage(start: number | string) {
    const startDateInS = Number(moment.utc(start).format('X'));
    this.billingTenure = getRollingDates(startDateInS);
  }

  private updateDatesBillingPage(start: number, end: number) {
    this.billingTenure = { startDate: start * 1000, endDate: end * 1000 };
  }

  private updateTenantInfo(onSuccess: () => void) {
    const { userStore, messagesStore } = this.rootStore;
    const { token } = userStore;
    apiAuthCaller(token)
      .get('/tenant')
      .then((resp) => {
        this.tenantInfo = resp.data;
        onSuccess();
      })
      .catch(() => {
        messagesStore.showErrorMessage('Failed to fetch new plan info. Please refresh the app.');
      });
  }

  @action.bound
  private getAllPlans() {
    const {
      userStore: { token },
    } = this.rootStore;
    apiAuthCaller(token)
      .get('/billing/plans')
      .then((response) => {
        this.pricing = serverPricingToClient(response.data);
      });
  }

  public async redirectToBilling() {
    try {
      const {
        userStore: { token },
      } = this.rootStore;
      const resp: AxiosResponse<IBillingURL> = await apiAuthCaller(token).get(
        `/billing/url?callbackUrl=${window.location.href}`,
      );
      window.location.href = resp.data.url;
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to redirect to payment page');
      this.rootStore.messagesStore.showErrorMessage(message);
    }
  }

  public convertToFreePlan(onSuccess: () => void, onError: () => void) {
    const {
      userStore: { token },
      messagesStore,
    } = this.rootStore;
    apiAuthCaller(token)
      .put('/billing/plan', { type: 'downgrade' })
      .then((response) => {
        this.updateTenantInfo(onSuccess);
        this.updateBillingInfo(response.data);
      })
      .catch((err) => {
        onError();
        const message = getApiErrorMessage(err, 'Failed to downgrade plan');
        messagesStore.showErrorMessage(message);
      });
  }

  public convertToProPlan(onSuccess: (setupIntent: SetupIntentInfo) => void, onError: () => void) {
    const {
      userStore: { token },
      messagesStore,
    } = this.rootStore;
    const address = {
      city: this.invoiceInfo.address.city,
      line1: this.invoiceInfo.address.street,
      line2: this.invoiceInfo.address.unit,
      postal_code: this.invoiceInfo.address.postalCode,
      state: this.invoiceInfo.address.state,
      country: this.invoiceInfo.address.country,
    };
    if (!this.cardToken) {
      messagesStore.showErrorMessage('Failed to convert to pro plan');
      return;
    }
    apiAuthCaller(token)
      .put('/billing/plan', {
        token: this.cardToken.id,
        type: 'upgrade',
        invoiceInfo: { address, name: this.invoiceInfo.company },
      })
      .then((response) => {
        this.updateBillingInfo(response.data);
        this.updateTenantInfo(() => onSuccess(response.data.pendingSetupIntentInfo));
        // onSuccess(response.data.pendingSetupIntentInfo);
      })
      .catch((err) => {
        onError();
        const message = getApiErrorMessage(err, 'Failed to convert to pro plan');
        messagesStore.showErrorMessage(message);
      });
  }

  @computed get getBillingDate() {
    if (this.billingTenure && this.billingTenure.endDate) {
      return moment(Number(this.billingTenure.endDate));
    }
    return undefined;
  }

  @action.bound
  public async getTotalEventsByCategory(onSuccess: () => void, onError: () => void) {
    const {
      userStore: { token },
    } = this.rootStore;
    if (this.billingTenure?.startDate && this.billingTenure.endDate) {
      const dates = {
        startDate: formatDate(this.billingTenure?.startDate),
        endDate: formatDate(this.billingTenure?.endDate),
      };
      try {
        const resp: AxiosResponse<EventsByType> = await apiAuthCaller(token).get('/totalEvents', {
          params: dates,
        });
        this.totalEventsByCategory = resp.data;
        this.getPercentages();
        onSuccess();
      } catch (error) {
        onError();
      }
    }
  }

  @computed get hasNewBilling() {
    return this.version === 'v2';
  }

  public saveCardToken(token: ICardToken) {
    this.cardToken = token;
  }

  @action.bound
  public getPaymentHistory() {
    const {
      userStore: { token },
      messagesStore,
    } = this.rootStore;
    apiAuthCaller(token)
      .get('/payments')
      .then((res) => {
        this.payments = res.data;
      })
      .catch((err) => {
        const message = getApiErrorMessage(err, 'Failed to get payment history');
        messagesStore.showErrorMessage(message);
      });
  }

  @action.bound
  public updateInvoiceDetails(invoiceInfo: InvoiceInfo) {
    this.invoiceInfo = invoiceInfo;
  }

  @action.bound
  public async submitInvoiceInfo(invoiceInfo: InvoiceInfo, emails: string[]) {
    const {
      userStore: { token },
      messagesStore,
    } = this.rootStore;
    const address = {
      city: invoiceInfo.address.city,
      line1: invoiceInfo.address.street,
      line2: invoiceInfo.address.unit,
      postal_code: invoiceInfo.address.postalCode,
      state: invoiceInfo.address.state,
      country: invoiceInfo.address.country,
    };
    try {
      await apiAuthCaller(token).put('/billing/invoiceInfo', {
        invoiceInfo: { address, name: invoiceInfo.company },
        emails,
      });
      this.invoiceInfo = invoiceInfo;
      this.emails = emails;
      messagesStore.showSuccessMessage('Invoice info updated');
    } catch (e) {
      const message = getApiErrorMessage(
        e,
        'Failed to update Invoice info. Try again in sometime.',
      );
      messagesStore.showErrorMessage(message);
    }
  }

  @action.bound
  public async submitCardInfo(
    cardToken: ICardToken,
    onSuccess: (setupIntent: SetupIntentInfo) => void,
  ) {
    const {
      userStore: { token },
      messagesStore,
    } = this.rootStore;
    try {
      const response = await apiAuthCaller(token).put('/billing/paymentMethod', {
        token: cardToken.id,
      });
      this.saveCardToken(cardToken);
      await onSuccess(response.data.pendingSetupIntentInfo);
      messagesStore.showSuccessMessage('Card updated');
    } catch (e) {
      const message = getApiErrorMessage(e, 'Failed to update card. Try again in sometime.');
      messagesStore.showErrorMessage(message);
    }
  }
}
